/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import InlineMessage from '@atlaskit/inline-message';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';
import { validateManagedBlockConfig } from '@atlassian/ai-managed-objects/validation';

import { messages } from '../../common/messages';

import { AimoStatusMetadata } from './AimoStatusMetadata';

const wrapperStyles = css({
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: token('elevation.surface'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
	boxShadow: token('elevation.shadow.overlay'),
});

const labelStyles = css({
	display: 'block',
	color: token('color.text.subtle'),
	marginBottom: token('space.075'),
});

const footerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
});

const actionsStyles = css({
	marginLeft: 'auto',
});

export type ConfigurePanelProps = {
	/** Hydrated AI-managed object metadata for the configured block. */
	aimoData?: AiManagedObject | null;
	/** Site cloud ID, used for profilecard hydration. */
	cloudId?: string;
	/** If set, an error message to display inline at the bottom of the panel. */
	error?: string | null;
	/** Initial value for the prompt textarea. */
	initialPrompt: string;
	/** If true, the panel is currently performing an async operation. */
	isSaving?: boolean;
	/** Called when the user dismisses the panel. */
	onCancel?: () => void;
	/**
	 * Called when the user clicks **Save**. The persistence and the underlying ProseMirror
	 * transaction are orchestrated by the node view above it.
	 * Returns a promise so the panel can show a loading spinner while saving.
	 */
	onSave: (prompt: string) => Promise<void> | void;
};

/**
 * Inline "Configure block" panel rendered directly underneath an
 * agent-managed bodied extension.
 */
export const ConfigurePanel = ({
	aimoData = null,
	cloudId,
	initialPrompt,
	onSave,
	onCancel,
	isSaving = false,
	error = null,
}: ConfigurePanelProps): JSX.Element => {
	const intl = useIntl();
	const [value, setValue] = useState<string>(initialPrompt);
	const lastHydratedPromptRef = useRef(initialPrompt);
	const textareaRef = useRef<HTMLTextAreaElement | null>(null);

	// Auto-focus immediately after inserting the block. `preventScroll`
	// avoids the browser scrolling to the portal mount point at the top of the page.
	useEffect(() => {
		textareaRef.current?.focus({ preventScroll: true });
	}, []);

	// Keep untouched local state in sync with external resets (e.g. fetched AIMO config),
	// without replacing a draft the user has already started typing.
	useEffect(() => {
		setValue((currentValue) => {
			const lastHydratedPrompt = lastHydratedPromptRef.current;
			lastHydratedPromptRef.current = initialPrompt;
			return currentValue === lastHydratedPrompt ? initialPrompt : currentValue;
		});
	}, [initialPrompt]);

	const handleSave = useCallback(() => {
		const trimmed = value.trim();
		const validation = validateManagedBlockConfig({ prompt: trimmed });
		if (!validation.valid) {
			// Validation currently only checks prompt is non-empty.
			// The save handler in the popup will also validate, but
			// we prevent the call here as an early guard.
			return;
		}
		onSave(trimmed);
	}, [onSave, value]);

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			setValue(event.currentTarget.value);
		},
		[setValue],
	);

	const isSaveDisabled = isSaving || value.trim().length === 0;

	return (
		<div css={wrapperStyles} data-testid="agent-managed-extension-configure-panel">
			<Stack space="space.150">
				<Heading size="small">{intl.formatMessage(messages.configureBlock)}</Heading>
				<div>
					<label css={labelStyles} htmlFor="agent-managed-extension-prompt">
						{intl.formatMessage(messages.customInstructionsLabel)}
					</label>
					<TextArea
						id="agent-managed-extension-prompt"
						testId="agent-managed-extension-prompt"
						ref={textareaRef}
						minimumRows={3}
						value={value}
						onChange={handleChange}
						placeholder={intl.formatMessage(messages.customInstructionsPlaceholder)}
						isDisabled={isSaving}
					/>
				</div>
				{error && (
					<InlineMessage appearance="error" testId="agent-managed-extension-error">
						<Text>{error}</Text>
					</InlineMessage>
				)}
				<div css={footerStyles}>
					<AimoStatusMetadata aimoData={aimoData} cloudId={cloudId} />
					<div css={actionsStyles}>
						<Inline space="space.100" alignInline="end">
							{onCancel && (
								<Button appearance="subtle" onClick={onCancel} isDisabled={isSaving}>
									{intl.formatMessage(messages.cancel)}
								</Button>
							)}
							<Button
								appearance="primary"
								testId="agent-managed-extension-save"
								onClick={handleSave}
								isDisabled={isSaveDisabled}
								iconAfter={isSaving ? () => <Spinner size="small" /> : undefined}
							>
								{intl.formatMessage(messages.save)}
							</Button>
						</Inline>
					</div>
				</div>
			</Stack>
		</div>
	);
};
